import { TIMEOUT_DEFAULT_MS } from '@/api/timeouts'
import { isDemoDeployment } from '@/utils/deployment'
import { apiCall } from './axiosInstance'
import { streamsApi as demoStreamsApi } from './demo/streamsApi'

import type { DependencyObject } from './deviceApi'

const DEFAULT_POLL_INTERVAL_MS = 500

export interface StreamField {
	name: string
	type: string
	formatter: string | null
	dependency?: DependencyObject
}

export type Stream = {
	name: string
	fields: StreamField[]
	dependency?: DependencyObject
}

export type ListStreamsResponse = {
	streams: Stream[]
}

/** Lightweight per-stream metadata for list views — no `fields` array. */
export type StreamSummary = {
	name: string
	dependency?: DependencyObject
	fields_count: number
}

export type ListStreamSummariesResponse = {
	streams: StreamSummary[]
}

export type CreateStreamRequest = {
	fields: StreamField[]
}

export type CreateStreamFieldRequest = {
	type: string
	formatter: string | null
}

/** Identical shape to `DeviceFieldCondition`, minus the operators the stream grammar doesn't support. */
export interface StreamFieldCondition {
	eq?: string
	ne?: string
	in?: string[]
	/** Case-insensitive substring match — what the search box sends. */
	regex?: string
}

/**
 * Recursive filter type for `POST /v3/streams/search` and `/v3/streams/filter-options`.
 *
 * Confirmed against the live backend's own deserialization error (not the doc-comment spec, which was
 * wrong on this): the accepted discriminants are exactly `and`, `or`, `not`, `stream_name`, `field_name`,
 * `field_type` — no bare boolean literal, and `field_name`/`field_type` are independent top-level
 * conditions, not a nested `{ name, type }` pair scoped to one field. There is no `dependency` variant —
 * filtering streams by "used by X" isn't currently supported by this endpoint.
 */
export type StreamSearchFilters =
	| { and: StreamSearchFilters[] }
	| { or: StreamSearchFilters[] }
	| { not: StreamSearchFilters }
	| { stream_name: StreamFieldCondition }
	| { field_name: StreamFieldCondition }
	| { field_type: StreamFieldCondition }

export type StreamSearchSortBy = 'name' | 'fields_count'
export type StreamSearchOrder = 'asc' | 'desc'

export interface StreamSearchPayload {
	filters?: StreamSearchFilters
	page: number
	per_page: number
	/** Default 'name'. */
	sort_by?: StreamSearchSortBy
	/** Default 'asc'. */
	order?: StreamSearchOrder
	/** When true, each row also carries its `fields` array. Default false. */
	include_fields?: boolean
}

export interface StreamSearchResult {
	name: string
	fields_count: number
	dependency?: DependencyObject
	/** Present only when the request set `include_fields: true`. */
	fields?: StreamField[]
}

export interface StreamSearchResponse {
	/** Total matching the filters, across all pages. */
	count: number
	streams: StreamSearchResult[]
}

/** `column` payload for `POST /v3/streams/filter-options`. `field_type` is modeled for completeness but
 *  not currently surfaced in the UI. */
export type StreamFilterOptionsColumn =
	| { kind: 'name' }
	| { kind: 'field'; stream?: string }
	| { kind: 'field_type'; stream?: string }

export interface StreamFilterOptionsPayload {
	column: StreamFilterOptionsColumn
	/** ILIKE substring match over the value being listed. */
	search?: string
	page: number
	per_page: number
	/** Narrows to the streams already filtered in the UI. */
	filters?: StreamSearchFilters
}

export interface StreamFilterOptionsResponse {
	column: StreamFilterOptionsColumn
	/** Total distinct values before paging. */
	count: number
	/** `string[]` for 'name'/'field_type'; `{name,type}[]` for 'field' — type travels with the name. */
	values: string[] | Array<{ name: string; type: string }>
}

export type SchemaUpdateTask = {
	target: string
	status: 'pending' | 'in_progress' | 'completed' | 'failed'
	remarks: string | null
}

export type SchemaUpdate = {
	id: string
	kind: string
	created_at: string
	info: unknown
	tasks: SchemaUpdateTask[]
}

export type SchemaUpdatesResponse = {
	count: number
	data: SchemaUpdate[]
}

const v3StreamsApi = {
	getStreams: async (page = 1, perPage = 100, tenantId?: string): Promise<ListStreamsResponse> =>
		await apiCall(`/v3/streams?page=${page}&per_page=${perPage}`, {
			...(tenantId && { headers: { 'x-bytebeam-tenant': tenantId, 'x-bytebeam-impersonate': 'admin' } }),
		}),
	/**
	 * Lightweight stream list for list/table views — skips the full `fields` array per stream
	 * (name, dependency, fields_count only). Use `getStream` for a single stream's full schema, or
	 * `searchStreams` for server-side filtering/sorting/pagination.
	 */
	getStreamSummaries: async (page = 1, perPage = 100, tenantId?: string): Promise<ListStreamSummariesResponse> =>
		await apiCall(`/v3/streams?page=${page}&per_page=${perPage}&names_only=true`, {
			...(tenantId && { headers: { 'x-bytebeam-tenant': tenantId, 'x-bytebeam-impersonate': 'admin' } }),
		}),
	/**
	 * Server-side filtered/sorted/paginated stream list. See `StreamSearchFilters` for the filter
	 * grammar (mirrors `DeviceSearchFilters`). Pass `include_fields: true` to get each row's `fields`
	 * array in the same response, instead of one `getStream` per row.
	 * POST /v3/streams/search
	 */
	searchStreams: async (
		payload: StreamSearchPayload,
		tenantId?: string,
		signal?: AbortSignal,
	): Promise<StreamSearchResponse> =>
		await apiCall('/v3/streams/search', {
			method: 'POST',
			data: payload,
			signal,
			...(tenantId && { headers: { 'x-bytebeam-tenant': tenantId, 'x-bytebeam-impersonate': 'admin' } }),
		}),
	/**
	 * Distinct values for one filterable column (stream name or field name), ILIKE-searched and
	 * paginated, optionally narrowed by the filters already applied in the UI — the same contract as
	 * `POST /v3/devices/filter-options`.
	 * POST /v3/streams/filter-options
	 */
	getStreamFilterOptions: async (
		payload: StreamFilterOptionsPayload,
		tenantId?: string,
		signal?: AbortSignal,
	): Promise<StreamFilterOptionsResponse> =>
		await apiCall('/v3/streams/filter-options', {
			method: 'POST',
			data: payload,
			signal,
			...(tenantId && { headers: { 'x-bytebeam-tenant': tenantId, 'x-bytebeam-impersonate': 'admin' } }),
		}),
	getStream: async (streamName: string, tenantId?: string): Promise<Stream> =>
		await apiCall(`/v3/streams/${encodeURIComponent(streamName)}`, {
			...(tenantId && { headers: { 'x-bytebeam-tenant': tenantId, 'x-bytebeam-impersonate': 'admin' } }),
		}),
	/**
	 * Create a new stream
	 * POST /v3/streams/{name}
	 * Body: { fields: [{name, type, formatter}] }
	 */
	createStream: async (
		streamName: string,
		fields: CreateStreamRequest['fields'],
		tenantId?: string,
	): Promise<{ status: string; name: string }> =>
		await apiCall(`/v3/streams/${encodeURIComponent(streamName)}`, {
			method: 'POST',
			data: { fields },
			...(tenantId && { headers: { 'x-bytebeam-tenant': tenantId, 'x-bytebeam-impersonate': 'admin' } }),
		}),

	/**
	 * Create a stream field (V3 API)
	 * POST /v3/streams/{stream_name}/fields/{field_name}
	 * Body: { type, formatter }
	 */
	createStreamField: async (
		streamName: string,
		fieldName: string,
		fieldType: string,
		formatter: string | null,
		tenantId?: string,
	): Promise<{ stream_name: string; field_name: string }> =>
		await apiCall(`/v3/streams/${encodeURIComponent(streamName)}/fields/${encodeURIComponent(fieldName)}`, {
			method: 'POST',
			data: {
				type: fieldType,
				formatter,
			},
			...(tenantId && { headers: { 'x-bytebeam-tenant': tenantId, 'x-bytebeam-impersonate': 'admin' } }),
		}),

	/**
	 * Update stream field (V3 API)
	 * PATCH /v3/streams/{stream_name}/fields/{field_name}
	 * Can update type and/or formatter - only sends fields that are provided
	 */
	updateStreamField: async (
		streamName: string,
		fieldName: string,
		updates: { type?: string; formatter?: string | null },
		tenantId?: string,
	): Promise<{ status: string }> => {
		const data: { type?: string; formatter?: string | null } = {}
		if (updates.type !== undefined) {
			data.type = updates.type
		}
		if (updates.formatter !== undefined) {
			data.formatter = updates.formatter
		}
		return await apiCall(`/v3/streams/${encodeURIComponent(streamName)}/fields/${encodeURIComponent(fieldName)}`, {
			method: 'PATCH',
			data,
			...(tenantId && { headers: { 'x-bytebeam-tenant': tenantId, 'x-bytebeam-impersonate': 'admin' } }),
		})
	},

	deleteStreamField: async (streamName: string, fieldName: string, tenantId?: string): Promise<{ status: string }> =>
		await apiCall(`/v3/streams/${encodeURIComponent(streamName)}/fields/${encodeURIComponent(fieldName)}`, {
			method: 'DELETE',
			...(tenantId && { headers: { 'x-bytebeam-tenant': tenantId, 'x-bytebeam-impersonate': 'admin' } }),
		}),
	deleteStream: async (streamName: string, tenantId?: string): Promise<{ status: string }> =>
		await apiCall(`/v3/streams/${encodeURIComponent(streamName)}`, {
			method: 'DELETE',
			...(tenantId && { headers: { 'x-bytebeam-tenant': tenantId, 'x-bytebeam-impersonate': 'admin' } }),
		}),

	getSchemaUpdates: async (
		active?: boolean,
		page = 1,
		perPage = 20,
		tenantId?: string,
	): Promise<SchemaUpdatesResponse> => {
		const activeParam = active !== undefined ? `&active=${active}` : ''
		return await apiCall(`/v3/schema-updates?page=${page}&per_page=${perPage}${activeParam}`, {
			...(tenantId && { headers: { 'x-bytebeam-tenant': tenantId, 'x-bytebeam-impersonate': 'admin' } }),
		})
	},
}

/**
 * Wait for any active schema updates to complete before proceeding.
 * Polls the schema-updates endpoint until no pending/in_progress jobs exist.
 * `tenantId` is required at org level, where the URL carries no tenant for the interceptor to
 * derive one from and the endpoint answers 400.
 */
export async function waitForSchemaUpdateCompletion({
	maxWaitMs = TIMEOUT_DEFAULT_MS,
	pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
	signal,
	tenantId,
}: {
	maxWaitMs?: number
	pollIntervalMs?: number
	signal?: AbortSignal
	tenantId?: string
} = {}): Promise<void> {
	const startTime = Date.now()

	while (Date.now() - startTime < maxWaitMs) {
		if (signal?.aborted) {
			return
		}

		try {
			const response = await v3StreamsApi.getSchemaUpdates(true, 1, 1, tenantId)
			if (response.count === 0) {
				return
			}
			// Wait before polling again, but allow abort during wait
			await new Promise<void>((resolve, reject) => {
				const abortHandler = () => {
					clearTimeout(timeoutId)
					reject(new DOMException('Aborted', 'AbortError'))
				}
				const timeoutId = setTimeout(() => {
					signal?.removeEventListener('abort', abortHandler)
					resolve()
				}, pollIntervalMs)
				signal?.addEventListener('abort', abortHandler, { once: true })
			})
		} catch (error) {
			if (error instanceof DOMException && error.name === 'AbortError') {
				return
			}
			// If we can't check, assume it's safe to proceed after a small delay
			await new Promise(resolve => setTimeout(resolve, pollIntervalMs))
			return
		}
	}

	// Timeout - proceed anyway and let the API return the error if still in progress
	console.warn('Timeout waiting for schema update completion')
}

export const streamsApi = isDemoDeployment() ? demoStreamsApi : v3StreamsApi
